import React from "react";
import { connect, MapDispatchToProps, MapStateToProps, MergeProps } from "react-redux";

import { composeQA } from "@multiweb/reactUtils";
import { IAppState, QAProps } from "@multiweb/types/react";
import { createMenuItemActivatedAction } from "../../actions";
import { isFormItemInstanceFavoriteActionEnabled } from "../../favoriteItems";
import { isActionItemFocused, isItemFocused, MenuFocusableItemType, NavigationSection } from "../../navigation";
import { getFormInstanceLabel, getOpenedFormFormCode, isOpenedItemActive } from "../../openedItems";
import { isFormItemInstanceCloseActionEnabled } from "../../selectors";
import { MenuItemActivatedType } from "../../types";
import { createMenuItemKeyActivationHandler, createOpenableInTabItemMouseActivationHandler } from "../../utils";
import { MenuItemComponent, MenuItemComponentProps } from "../MenuItemComponent";
import { OpenedFormRightPartComponent } from "../OpenedFormRightPartComponent";

export type OpenedFormProps = {
    /** Identifikátor otevřené instance formuláře. */
    formInstanceId: string;
    /** Sekce, ve které je položka vykreslená. Slouží pro určování zaměření. */
    section: NavigationSection;
} & QAProps;

type StateProps = {
    label: string | undefined;
    isActionItemFocused: boolean;
    isFavoriteActionEnabled: boolean;
    isCloseActionEnabled: boolean;
    formCode?: string;
} & Pick<MenuItemComponentProps, "isFocused" | "isActive">;

type DispatchProps = {
    openedFormActivated: typeof createMenuItemActivatedAction;
};

const mapStateToProps: MapStateToProps<StateProps, OpenedFormProps, IAppState> = (state, { formInstanceId, section }) => ({
    label: getFormInstanceLabel(state, formInstanceId),
    isFocused: isItemFocused(state, { itemId: formInstanceId, type: MenuFocusableItemType.openedItem, section }),
    isActionItemFocused: isActionItemFocused(state, { parentItemId: formInstanceId, section }),
    isActive: isOpenedItemActive(state, formInstanceId),
    isFavoriteActionEnabled: isFormItemInstanceFavoriteActionEnabled(state, formInstanceId),
    isCloseActionEnabled: isFormItemInstanceCloseActionEnabled(state, formInstanceId),
    formCode: getOpenedFormFormCode(state, formInstanceId),
});

const mapDispatchToProps: MapDispatchToProps<DispatchProps, OpenedFormProps> = {
    openedFormActivated: createMenuItemActivatedAction,
};

const mergeProps: MergeProps<StateProps, DispatchProps, OpenedFormProps, MenuItemComponentProps> = (
    { label, isFocused, isActionItemFocused, isActive, isFavoriteActionEnabled, isCloseActionEnabled, formCode },
    { openedFormActivated },
    { formInstanceId, qa, section },
) => ({
    label,
    onClick: createOpenableInTabItemMouseActivationHandler(formInstanceId, MenuItemActivatedType.openedForm, section, openedFormActivated),
    onKeyUp: createMenuItemKeyActivationHandler(formInstanceId, MenuItemActivatedType.openedForm, section, openedFormActivated),
    rightPartComponentCreator: (isActive) => <OpenedFormRightPartComponent {...{ isActive, formInstanceId, isActionItemFocused, isFavoriteActionEnabled, isCloseActionEnabled, section }} />,
    isFocused,
    isActive,
    qa: composeQA(composeQA(formInstanceId, formCode), qa),
});

/**
 * Získává data pro zobrazení položky otevřeného formuláře, který nemá položku v menu.
 */
export const OpenedForm = connect(mapStateToProps, mapDispatchToProps, mergeProps)(MenuItemComponent);
